/**
 * The screen a page really gets, which on a phone is not the window.
 *
 * A browser's own furniture eats into it and no CSS unit says how much is left
 * right now: `vh` is the tall window with the URL bar hidden (so a page sized
 * in it hangs off the bottom until the bar goes), `dvh` jumps about under the
 * keyboard, and `window.innerHeight` means one thing on Android and another on
 * iOS. So measure it once, here, write it into `--screen-w` and `--screen-h`
 * for the stylesheet, and hand the same numbers to whatever draws: a canvas has
 * to agree with its CSS box or the picture stretches.
 *
 * The keyboard is kept out of that size, since a field opening would otherwise
 * squash the game to half a screen and let it go again. How much of the bottom
 * it covers goes in `--screen-keyboard` instead, for the few things (a chat
 * box) that have to sit above it.
 *
 * Full screen, where a browser lets a page ask for it, is the real answer to a
 * URL bar: see goFullscreen.
 */

export interface ScreenSize {
  readonly w: number;
  readonly h: number;
}

let size: ScreenSize = { w: 0, h: 0 };
let keyboard = 0;
let watching = false;
let queued = false;
const listeners = new Set<(size: ScreenSize) => void>();

/** The drawable screen in CSS px, with the keyboard (which covers it rather than taking it) left in. */
export function screenSize(): ScreenSize {
  if (!watching) watch();
  return size;
}

export function screenW(): number {
  return screenSize().w;
}

export function screenH(): number {
  return screenSize().h;
}

/** How much of the bottom the soft keyboard is covering, in CSS px: 0 with none up. */
export function keyboardInset(): number {
  screenSize();
  return keyboard;
}

/** Follow the screen: the callback runs now and again whenever it changes. Returns a stop. */
export function onScreenResize(fn: (size: ScreenSize) => void): () => void {
  const first = screenSize();
  listeners.add(fn);
  fn(first);
  return () => void listeners.delete(fn);
}

/** Whether this browser lets a page ask for full screen at all: iPhones don't (iPads and Android do). */
export function canGoFullscreen(): boolean {
  return !!document.fullscreenEnabled;
}

export function isFullscreen(): boolean {
  return !!document.fullscreenElement;
}

/** A touch screen, by the pointer that moves over it: a phone or a tablet rather than a mouse. */
export function coarsePointer(): boolean {
  return matchMedia("(pointer: coarse)").matches;
}

/**
 * Full screen, the one thing that puts a phone's URL bar away for good, and
 * sideways with it where the browser lets a page ask (Android does). Only works
 * from inside a tap or a key press, and a browser may refuse: says whether
 * we're full screen after.
 */
export async function goFullscreen(opts: { landscape?: boolean } = {}): Promise<boolean> {
  if (isFullscreen()) return true;
  if (!canGoFullscreen()) return false;
  try {
    await document.documentElement.requestFullscreen({ navigationUI: "hide" });
  } catch {
    return false;
  }
  if (opts.landscape) {
    const orientation = screen.orientation as ScreenOrientation & { lock?: (to: string) => Promise<void> };
    await orientation.lock?.("landscape").catch(() => {});
  }
  return true;
}

export async function leaveFullscreen(): Promise<void> {
  const orientation = screen.orientation as ScreenOrientation & { unlock?: () => void };
  orientation.unlock?.();
  if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
}

/** The full screen button: out if we're in it, in if we're not. */
export async function toggleFullscreen(opts: { landscape?: boolean } = {}): Promise<boolean> {
  if (isFullscreen()) {
    await leaveFullscreen();
    return false;
  }
  return goFullscreen(opts);
}

function watch(): void {
  watching = true;
  const bump = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      measure();
    });
  };
  window.addEventListener("resize", bump);
  window.addEventListener("orientationchange", bump);
  // The keyboard and the URL bar sliding away move the visual viewport without resizing the window.
  window.visualViewport?.addEventListener("resize", bump);
  window.visualViewport?.addEventListener("scroll", bump);
  document.addEventListener("fullscreenchange", bump);
  document.addEventListener("focusin", bump);
  document.addEventListener("focusout", bump);
  measure();
}

function measure(): void {
  const vv = window.visualViewport;
  const w = Math.round(vv?.width ?? window.innerWidth);
  const h = Math.round(vv?.height ?? window.innerHeight);
  const next = nextSize(size, { w, h }, typing());
  keyboard = Math.max(0, next.h - h);
  const style = document.documentElement.style;
  style.setProperty("--screen-w", `${next.w}px`);
  style.setProperty("--screen-h", `${next.h}px`);
  style.setProperty("--screen-keyboard", `${keyboard}px`);
  if (next.w === size.w && next.h === size.h) return;
  size = next;
  for (const fn of listeners) fn(size);
}

/**
 * The size to keep, given what we just measured: what we measured, unless
 * we're typing on a screen that has gone shorter without changing width,
 * which is a keyboard over the page rather than a smaller page. Holding the
 * size through that is the whole point: a field opening would otherwise halve
 * the game and give it back a moment later.
 */
export function nextSize(held: ScreenSize, measured: ScreenSize, typing: boolean): ScreenSize {
  const covered = typing && measured.w === held.w && measured.h < held.h;
  return covered ? held : measured;
}

function typing(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable);
}
